import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Tag,
  useColorModeValue,
  Icon,
  Circle,
  Flex,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { EXPERIENCE } from '@/constants/data';
import { containerVariant, slideInLeft, slideInRight } from '@/utils/animation';
import SectionDivider from './SectionDivider';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { BsBriefcaseFill } from 'react-icons/bs';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

interface ExperienceCardProps {
  position: string;
  company: string;
  duration: string;
  description: string;
  technologies: string[];
  index: number;
}

const ExperienceCard = ({ position, company, duration, description, technologies, index }: ExperienceCardProps) => {
  const bgColor = useColorModeValue('rgba(255, 255, 255, 0.9)', 'rgba(26, 32, 44, 0.8)');
  const borderColor = useColorModeValue('brand.200', 'brand.700');
  const tagBg = useColorModeValue('brand.50', 'brand.900');
  const tagColor = useColorModeValue('brand.600', 'brand.200');
  const textColor = useColorModeValue('gray.800', 'gray.200');
  const shadowColor = useColorModeValue('rgba(0, 163, 255, 0.1)', 'rgba(0, 163, 255, 0.2)');
  const accentColor = useColorModeValue('accent.500', 'accent.400');
  const isEven = index % 2 === 0;

  return (
    <MotionFlex
      variants={isEven ? slideInLeft : slideInRight}
      w="full"
      justify={isEven ? 'flex-start' : 'flex-end'}
      px={{ base: 4, md: 0 }}
    >
      <Box
        w={{ base: 'full', md: '80%' }}
        bg={bgColor}
        p={8}
        borderRadius="xl"
        border="1px solid"
        borderColor={borderColor}
        boxShadow={`0 4px 20px ${shadowColor}`}
        backdropFilter="blur(10px)"
        transition="all 0.3s ease-in-out"
        _hover={{
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 30px ${shadowColor}`,
          borderColor: accentColor
        }}
        position="relative"
        _before={{
          content: '""',
          position: 'absolute',
          width: '20px',
          height: '20px',
          background: bgColor,
          transform: 'rotate(45deg)',
          top: '30px',
          left: isEven ? '-10px' : 'auto',
          right: isEven ? 'auto' : '-10px',
          borderLeft: isEven ? `1px solid ${borderColor}` : 'none',
          borderBottom: isEven ? `1px solid ${borderColor}` : 'none',
          borderRight: !isEven ? `1px solid ${borderColor}` : 'none',
          borderTop: !isEven ? `1px solid ${borderColor}` : 'none',
        }}
      >
        <VStack align="start" spacing={4}>
          <HStack spacing={4}>
            <Circle 
              size="40px" 
              bg={tagBg} 
              color={tagColor}
              transition="all 0.3s ease-in-out"
              _hover={{
                transform: 'scale(1.1)',
                bg: accentColor,
                color: 'white'
              }}>
              <Icon as={BsBriefcaseFill} w={5} h={5} />
            </Circle>
            <Box>
              <Heading size="md">{position}</Heading>
              <Text color={textColor} fontSize="sm">
                {company}
              </Text>
            </Box>
          </HStack>

          <Text
            color={useColorModeValue('gray.500', 'gray.300')}
            fontSize="sm"
            fontWeight="medium"
          >
            {duration}
          </Text>

          <Text color={useColorModeValue('gray.600', 'gray.400')} fontSize="sm">
            {description}
          </Text>

          <HStack spacing={2} wrap="wrap">
            {technologies.map((tech) => (
              <Tag
                key={tech}
                size="sm"
                variant="subtle"
                bg={tagBg}
                color={tagColor}
              >
                {tech}
              </Tag>
            ))}
          </HStack>
        </VStack>
      </Box>
    </MotionFlex>
  );
};

export default function Experience() {
  const { ref, isVisible } = useIntersectionObserver();
  const bgGradient = useColorModeValue(
    'linear(to-r, accent.50, brand.50, highlight.50)',
    'linear(to-r, accent.900, brand.900, highlight.900)'
  );
  const timelineColor = useColorModeValue('brand.200', 'brand.700');

  return (
    <Box
      as="section"
      id="experience"
      py={24}
      borderTop="1px solid"
      borderColor={useColorModeValue('gray.200', 'gray.700')}
      bgGradient={useColorModeValue(
        'linear(to-tr, pink.50, orange.50)',
        'linear(to-tr, pink.900, orange.900)'
      )}
      boxShadow="inset 0 4px 8px rgba(0, 0, 0, 0.05)"
    >
      <Container maxW="7xl">
        <VStack spacing={12}>
          <Box textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '4xl' }}
              fontWeight="bold"
              mb={4}
            >
              Professional Experience
            </Heading>
            <Text
              fontSize={{ base: 'lg', md: 'xl' }}
              color={useColorModeValue('gray.600', 'gray.400')}
              maxW="2xl"
              mx="auto"
            >
              My journey in blockchain development and software engineering
            </Text>
          </Box>

          <MotionBox
            ref={ref}
            initial="initial"
            animate={isVisible ? 'animate' : 'initial'}
            variants={containerVariant}
            w="full"
            position="relative"
            _before={{
              content: '""',
              position: 'absolute',
              width: '2px',
              height: '100%',
              background: timelineColor,
              left: '50%',
              transform: 'translateX(-50%)',
              display: { base: 'none', md: 'block' },
            }}
          >
            <VStack spacing={8} position="relative">
              {EXPERIENCE.map((exp, index) => (
                <ExperienceCard
                  key={`${exp.company}-${exp.position}`}
                  {...exp}
                  index={index}
                />
              ))}
            </VStack>
          </MotionBox>
        </VStack>
      </Container>
    </Box>
  );
}
